// Host meeting controls (F-HOST-2 mute-all, F-HOST-4 permission gates,
// F-LOB-3 lock, F-HOST-5 end-for-all). Host/co-host only — this whole panel is
// gated behind isHost in the controls bar, and every action re-checks
// server-side.

import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { api, MeetingPolicy } from '../api';
import { probeRender } from '../perf/renderProbe';
import { pushToast } from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import { IconChevronLeft, IconClose, IconMicOff, IconStats } from '../ui/icons';
import { Toggle } from '../ui/Toggle';
import { ConnectionStats } from './ConnectionStats';

export const HostMenu = memo(function HostMenu({
  slug,
  onClose,
}: {
  slug: string;
  onClose: () => void;
}) {
  probeRender('HostMenu');
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<MeetingPolicy>({
    allowShare: true,
    allowUnmute: true,
    allowChat: true,
  });
  const [locked, setLocked] = useState(false);
  const [view, setView] = useState<'main' | 'stats'>('main');

  useEffect(() => {
    api
      .meetingState(slug)
      .then((st) => setLocked(st.locked))
      .catch(() => {});
  }, [slug]);

  const muteAll = useCallback(() => {
    void api
      .muteAll(slug)
      .then(() => pushToast('Everyone muted'))
      .catch(() => pushToast('Could not mute all', 'warning'));
  }, [slug]);

  const toggleLock = useCallback(() => {
    const next = !locked;
    setLocked(next);
    void api.setLock(slug, next).catch(() => {
      setLocked(!next);
      pushToast('Could not change lock', 'warning');
    });
  }, [slug, locked]);

  const togglePolicy = useCallback(
    (key: keyof MeetingPolicy) => {
      const next = { ...policy, [key]: !policy[key] };
      setPolicy(next);
      void api.setPolicy(slug, next).catch(() => pushToast('Could not update permissions', 'warning'));
    },
    [slug, policy],
  );

  const endForAll = useCallback(() => {
    void api.end(slug).then(() => navigate('/'));
  }, [slug, navigate]);
  const showStats = useCallback(() => setView('stats'), []);
  const showMain = useCallback(() => setView('main'), []);

  return (
    <div className="bz-panel bz-host-panel" role="region" aria-label="Host controls">
      <div className="bz-panel-header">
        {view === 'stats' ? (
          <button
            type="button"
            className="bz-mt-back"
            onClick={showMain}
            aria-label="Back to host controls"
          >
            <IconChevronLeft size={18} />
            <span>Connection stats</span>
          </button>
        ) : (
          <span>Host controls</span>
        )}
        <IconButton shape="flat" label="Close" onClick={onClose}>
          <IconClose size={14} />
        </IconButton>
      </div>
      {view === 'stats' ? (
        <div className="bz-host-menu-body">
          <ConnectionStats />
        </div>
      ) : (
      <div className="bz-host-menu-body">
        <button className="bz-host-btn" onClick={muteAll}>
          <IconMicOff size={18} />
          Mute everyone
        </button>

        <div className="bz-host-row">
          <span className="bz-host-row-label">
            Lock meeting <span className="bz-host-row-sub">(no new guests)</span>
          </span>
          <Toggle on={locked} onToggle={toggleLock} label="Lock meeting" />
        </div>

        <div className="bz-host-divider" />
        <div className="bz-host-section">Attendees may…</div>

        <PolicyRow label="Share their screen" on={policy.allowShare} k="allowShare" onToggle={togglePolicy} />
        <PolicyRow
          label="Unmute / turn on camera"
          on={policy.allowUnmute}
          k="allowUnmute"
          onToggle={togglePolicy}
        />
        <PolicyRow label="Send chat & reactions" on={policy.allowChat} k="allowChat" onToggle={togglePolicy} />

        <div className="bz-host-divider" />
        <button className="bz-host-btn" onClick={showStats}>
          <IconStats size={18} />
          Connection stats
        </button>

        <button className="bz-host-end" onClick={endForAll}>
          End meeting for everyone
        </button>
      </div>
      )}
    </div>
  );
});

const PolicyRow = memo(function PolicyRow({
  label,
  on,
  k,
  onToggle,
}: {
  label: string;
  on: boolean;
  k: keyof MeetingPolicy;
  onToggle: (k: keyof MeetingPolicy) => void;
}) {
  const toggle = useCallback(() => onToggle(k), [k, onToggle]);
  return (
    <div className="bz-host-row">
      <span className="bz-host-row-label">{label}</span>
      <Toggle on={on} onToggle={toggle} label={label} />
    </div>
  );
});
